import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Stack,
  Typography,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import { customerOrderService } from '../services/api';

const PERIOD_TYPE_OPTIONS = [
  { value: 'day', label: 'День' },
  { value: 'month', label: 'Місяць' },
  { value: 'quarter', label: 'Квартал' },
  { value: 'year', label: 'Рік' },
  { value: 'range', label: 'Діапазон' },
];

const MONTH_OPTIONS = [
  'Січень', 'Лютий', 'Березень', 'Квітень', 'Травень', 'Червень',
  'Липень', 'Серпень', 'Вересень', 'Жовтень', 'Листопад', 'Грудень',
].map((label, i) => ({ value: i + 1, label }));

const QUARTER_OPTIONS = [
  { value: 1, label: 'Q1 (січ–бер)' },
  { value: 2, label: 'Q2 (кві–чер)' },
  { value: 3, label: 'Q3 (лип–вер)' },
  { value: 4, label: 'Q4 (жов–гру)' },
];

function pad2(n) {
  return String(n).padStart(2, '0');
}

function toDateInputStr(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function formatAmount(value) {
  if (value == null) return '0,00 грн';
  return `${value.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} грн`;
}

function formatDateOnly(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('uk-UA');
}

function yearOptionsAround(centerYear) {
  const years = [];
  for (let y = centerYear - 3; y <= centerYear + 1; y += 1) years.push(y);
  return years;
}

/** Межі періоду [start, end] (включно) за типом і значеннями фільтра. */
function getPeriodRange(periodType, vals) {
  const y = Number(vals.year) || new Date().getFullYear();
  switch (periodType) {
    case 'day': {
      const d = vals.day || toDateInputStr(new Date());
      return { start: new Date(`${d}T00:00:00`), end: new Date(`${d}T23:59:59.999`) };
    }
    case 'month': {
      const m = Number(vals.month) || 1;
      return { start: new Date(y, m - 1, 1, 0, 0, 0), end: new Date(y, m, 0, 23, 59, 59, 999) };
    }
    case 'quarter': {
      const q = Number(vals.quarter) || 1;
      const startMonth = (q - 1) * 3;
      return {
        start: new Date(y, startMonth, 1, 0, 0, 0),
        end: new Date(y, startMonth + 3, 0, 23, 59, 59, 999),
      };
    }
    case 'year':
      return { start: new Date(y, 0, 1, 0, 0, 0), end: new Date(y, 11, 31, 23, 59, 59, 999) };
    case 'range': {
      const from = vals.from || toDateInputStr(new Date());
      const to = vals.to || from;
      return { start: new Date(`${from}T00:00:00`), end: new Date(`${to}T23:59:59.999`) };
    }
    default:
      return { start: new Date(y, 0, 1), end: new Date(y, 11, 31, 23, 59, 59, 999) };
  }
}

/**
 * Сегменти графіка, що покривають весь обраний діапазон [start, end].
 * Гранулярність: по днях для «День»/«Місяць»/короткого «Діапазону», по місяцях — для «Квартал»/«Рік»/довгого «Діапазону».
 */
function buildPeriodBuckets(periodType, start, end) {
  const spanDays = Math.round((end - start) / 86400000);
  const granularity =
    periodType === 'day' || periodType === 'month'
      ? 'day'
      : periodType === 'quarter' || periodType === 'year'
      ? 'month'
      : spanDays <= 62
      ? 'day'
      : 'month';

  const buckets = [];
  if (granularity === 'day') {
    const cur = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    const last = new Date(end.getFullYear(), end.getMonth(), end.getDate());
    while (cur <= last) {
      const y = cur.getFullYear();
      const m = cur.getMonth();
      const d = cur.getDate();
      buckets.push({
        label: `${y}-${pad2(m + 1)}-${pad2(d)}`,
        bucketStart: new Date(y, m, d, 0, 0, 0),
        bucketEnd: new Date(y, m, d, 23, 59, 59, 999),
      });
      cur.setDate(cur.getDate() + 1);
    }
  } else {
    const cur = new Date(start.getFullYear(), start.getMonth(), 1);
    const last = new Date(end.getFullYear(), end.getMonth(), 1);
    while (cur <= last) {
      const y = cur.getFullYear();
      const m = cur.getMonth();
      buckets.push({
        label: `${y}-${pad2(m + 1)}`,
        bucketStart: new Date(y, m, 1, 0, 0, 0),
        bucketEnd: new Date(y, m + 1, 0, 23, 59, 59, 999),
      });
      cur.setMonth(cur.getMonth() + 1);
    }
  }
  return { buckets, granularity };
}

/** Логарифмічна позиція значення в межах [0, 1] від низу шкали (0 -> 0). */
function logPct(value, maxTick) {
  if (value <= 0) return 0;
  return (Math.log10(value + 1) / Math.log10(maxTick + 1)) * 100;
}

function MiniBarChart({ data, title }) {
  const rawMax = Math.max(1, ...data.map((d) => Math.max(d.quantity, d.amount)));
  let maxTick = 1;
  const ticks = [1];
  while (maxTick < rawMax) {
    maxTick *= 10;
    ticks.push(maxTick);
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, flex: 1, minWidth: 320, display: 'flex', flexDirection: 'column' }}>
      <Typography variant="subtitle2" sx={{ mb: 1.5, textAlign: 'center', fontWeight: 600 }}>
        {title}
      </Typography>
      <Box sx={{ display: 'flex', flex: 1, minHeight: 360 }}>
        <Box
          sx={{
            position: 'relative',
            width: 56,
            flexShrink: 0,
            borderRight: '1px solid',
            borderColor: 'divider',
          }}
        >
          {ticks.map((t) => (
            <Typography
              key={t}
              variant="caption"
              color="text.secondary"
              sx={{
                position: 'absolute',
                bottom: `${logPct(t, maxTick)}%`,
                right: 6,
                transform: 'translateY(50%)',
                fontSize: '0.625rem',
                whiteSpace: 'nowrap',
              }}
            >
              {t.toLocaleString('uk-UA')}
            </Typography>
          ))}
        </Box>
        <Box sx={{ position: 'relative', flex: 1, overflowX: 'auto' }}>
          {ticks.map((t) => (
            <Box
              key={t}
              sx={{
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: `${logPct(t, maxTick)}%`,
                borderTop: '1px dashed',
                borderColor: 'divider',
              }}
            />
          ))}
          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.75, height: '100%', pl: 1 }}>
            {data.map((d) => (
              <Box
                key={d.label}
                sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 28, height: '100%' }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.25, flex: 1, width: '100%', justifyContent: 'center' }}>
                  <Tooltip title={`${d.label} · Кількість: ${d.quantity.toLocaleString('uk-UA')}`} arrow>
                    <Box
                      sx={{
                        width: 7,
                        height: `${logPct(d.quantity, maxTick)}%`,
                        minHeight: d.quantity > 0 ? 2 : 0,
                        bgcolor: 'teal',
                        borderRadius: 0.5,
                        cursor: 'default',
                      }}
                    />
                  </Tooltip>
                  <Tooltip title={`${d.label} · Сума: ${formatAmount(d.amount)}`} arrow>
                    <Box
                      sx={{
                        width: 11,
                        height: `${logPct(d.amount, maxTick)}%`,
                        minHeight: d.amount > 0 ? 2 : 0,
                        bgcolor: 'warning.main',
                        borderRadius: 0.5,
                        cursor: 'default',
                      }}
                    />
                  </Tooltip>
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 0.5, fontSize: '0.6rem', whiteSpace: 'nowrap' }}
                >
                  {d.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
      <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 1 }}>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Box sx={{ width: 10, height: 10, bgcolor: 'teal', borderRadius: 0.5 }} />
          <Typography variant="caption">Кількість</Typography>
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Box sx={{ width: 10, height: 10, bgcolor: 'warning.main', borderRadius: 0.5 }} />
          <Typography variant="caption">Сума</Typography>
        </Stack>
      </Stack>
    </Paper>
  );
}

function MetricsTable({ rows }) {
  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Показник</TableCell>
            <TableCell align="right">Значення</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.label}>
              <TableCell>{r.label}</TableCell>
              <TableCell align="right">{r.value}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function OrdersAnalyticsPage({ report = 'orders' }) {
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [periodType, setPeriodType] = useState('month');
  const now = new Date();
  const [periodVals, setPeriodVals] = useState({
    day: toDateInputStr(now),
    year: now.getFullYear(),
    month: now.getMonth() + 1,
    quarter: Math.floor(now.getMonth() / 3) + 1,
    from: toDateInputStr(now),
    to: toDateInputStr(now),
  });
  const [drillCustomer, setDrillCustomer] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await customerOrderService.getAll();
        const list = Array.isArray(res.data?.results) ? res.data.results : Array.isArray(res.data) ? res.data : [];
        if (!cancelled) setOrders(list);
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const lineEntries = useMemo(() => {
    const out = [];
    (orders || []).forEach((o) => {
      (o.lines || []).forEach((l) => {
        const qty = Number(l.quantity) || 0;
        const price = l.price != null && l.price !== '' ? Number(l.price) : null;
        out.push({
          order: o,
          line: l,
          quantity: qty,
          amount: price != null ? qty * price : null,
        });
      });
    });
    return out;
  }, [orders]);

  const { start, end } = useMemo(() => getPeriodRange(periodType, periodVals), [periodType, periodVals]);
  const chartYear = start.getFullYear();

  const filteredEntries = useMemo(() => {
    return lineEntries.filter((e) => {
      const d = e.order.order_date ? new Date(e.order.order_date) : null;
      if (!d || Number.isNaN(d.getTime())) return false;
      if (d < start || d > end) return false;
      if (report === 'customers' && drillCustomer && e.order.customer_name !== drillCustomer) return false;
      return true;
    });
  }, [lineEntries, start, end, report, drillCustomer]);

  const customerOptions = useMemo(() => {
    const set = new Set();
    lineEntries.forEach((e) => {
      const d = e.order.order_date ? new Date(e.order.order_date) : null;
      if (d && d >= start && d <= end && e.order.customer_name) set.add(e.order.customer_name);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [lineEntries, start, end]);

  const sumQty = (entries) => entries.reduce((s, e) => s + e.quantity, 0);
  const sumAmount = (entries) => entries.reduce((s, e) => s + (e.amount || 0), 0);

  const { buckets: chartBuckets, granularity: chartGranularity } = useMemo(
    () => buildPeriodBuckets(periodType, start, end),
    [periodType, start, end],
  );

  const chartRows = useMemo(() => {
    return chartBuckets.map((b) => {
      const entries = lineEntries.filter((e) => {
        const d = e.order.order_date ? new Date(e.order.order_date) : null;
        if (!d) return false;
        if (d < b.bucketStart || d > b.bucketEnd) return false;
        if (report === 'customers' && drillCustomer && e.order.customer_name !== drillCustomer) return false;
        return true;
      });
      return {
        label: b.label,
        quantity: sumQty(entries),
        amount: sumAmount(entries),
        positions: entries.length,
      };
    });
  }, [chartBuckets, lineEntries, report, drillCustomer]);

  const byQuarter = useMemo(() => {
    const rows = [];
    for (let q = 1; q <= 4; q += 1) {
      const startMonth = (q - 1) * 3;
      const entries = lineEntries.filter((e) => {
        const d = e.order.order_date ? new Date(e.order.order_date) : null;
        if (!d) return false;
        if (d.getFullYear() !== chartYear) return false;
        const m = d.getMonth();
        if (m < startMonth || m >= startMonth + 3) return false;
        if (drillCustomer && e.order.customer_name !== drillCustomer) return false;
        return true;
      });
      rows.push({ label: `Q${q}`, quantity: sumQty(entries), amount: sumAmount(entries), positions: entries.length });
    }
    return rows;
  }, [lineEntries, chartYear, drillCustomer]);

  const byYear = useMemo(() => {
    const entries = lineEntries.filter((e) => {
      const d = e.order.order_date ? new Date(e.order.order_date) : null;
      if (!d || d.getFullYear() !== chartYear) return false;
      if (drillCustomer && e.order.customer_name !== drillCustomer) return false;
      return true;
    });
    return [{ label: String(chartYear), quantity: sumQty(entries), amount: sumAmount(entries), positions: entries.length }];
  }, [lineEntries, chartYear, drillCustomer]);

  const byProduct = useMemo(() => {
    const map = new Map();
    filteredEntries.forEach((e) => {
      const key = e.line.product_name || `#${e.line.product}`;
      const row = map.get(key) || { name: key, quantity: 0, amount: 0, positions: 0, lastDate: null };
      row.quantity += e.quantity;
      row.amount += e.amount || 0;
      row.positions += 1;
      const d = e.order.order_date;
      if (d && (!row.lastDate || new Date(d) > new Date(row.lastDate))) row.lastDate = d;
      map.set(key, row);
    });
    return Array.from(map.values()).sort((a, b) => b.amount - a.amount);
  }, [filteredEntries]);

  const byCustomer = useMemo(() => {
    const map = new Map();
    filteredEntries.forEach((e) => {
      const key = e.order.customer_name || '—';
      const row = map.get(key) || { name: key, quantity: 0, amount: 0, positions: 0, lastDate: null };
      row.quantity += e.quantity;
      row.amount += e.amount || 0;
      row.positions += 1;
      const d = e.order.order_date;
      if (d && (!row.lastDate || new Date(d) > new Date(row.lastDate))) row.lastDate = d;
      map.set(key, row);
    });
    return Array.from(map.values()).sort((a, b) => b.amount - a.amount);
  }, [filteredEntries]);

  const ordersMetrics = useMemo(() => {
    const fullyShipped = filteredEntries.filter(
      (e) => e.line.quantity > 0 && (Number(e.line.shipped_quantity) || 0) >= e.line.quantity,
    ).length;
    const readyNotShipped = filteredEntries.filter(
      (e) =>
        e.line.production_status_display === 'Завершено' &&
        (Number(e.line.shipped_quantity) || 0) < e.line.quantity,
    ).length;
    return [
      { label: 'Позицій у періоді', value: filteredEntries.length },
      { label: 'Кількість виробів', value: sumQty(filteredEntries) },
      { label: 'Сума', value: formatAmount(sumAmount(filteredEntries)) },
      { label: 'Готово до відвантаження', value: readyNotShipped },
      { label: 'Відвантажено', value: fullyShipped },
    ];
  }, [filteredEntries]);

  const customersMetrics = useMemo(() => {
    const customersCount = new Set(filteredEntries.map((e) => e.order.customer_name)).size;
    const positions = filteredEntries.length;
    const amount = sumAmount(filteredEntries);
    return [
      { label: 'Замовників у періоді', value: customersCount },
      { label: 'Позицій у періоді', value: positions },
      { label: 'Кількість виробів', value: sumQty(filteredEntries) },
      { label: 'Сума', value: formatAmount(amount) },
      { label: 'Середній чек позиції', value: formatAmount(positions ? amount / positions : 0) },
    ];
  }, [filteredEntries]);

  const renderPeriodInputs = () => {
    const yearOpts = yearOptionsAround(now.getFullYear());
    switch (periodType) {
      case 'day':
        return (
          <TextField
            size="small"
            label="Дата"
            type="date"
            value={periodVals.day}
            onChange={(e) => setPeriodVals((p) => ({ ...p, day: e.target.value }))}
            InputLabelProps={{ shrink: true }}
          />
        );
      case 'month':
        return (
          <>
            <TextField
              select
              size="small"
              label="Рік"
              value={periodVals.year}
              onChange={(e) => setPeriodVals((p) => ({ ...p, year: e.target.value }))}
              sx={{ minWidth: 110 }}
            >
              {yearOpts.map((y) => (
                <MenuItem key={y} value={y}>
                  {y}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="Місяць"
              value={periodVals.month}
              onChange={(e) => setPeriodVals((p) => ({ ...p, month: e.target.value }))}
              sx={{ minWidth: 160 }}
            >
              {MONTH_OPTIONS.map((m) => (
                <MenuItem key={m.value} value={m.value}>
                  {m.label}
                </MenuItem>
              ))}
            </TextField>
          </>
        );
      case 'quarter':
        return (
          <>
            <TextField
              select
              size="small"
              label="Рік"
              value={periodVals.year}
              onChange={(e) => setPeriodVals((p) => ({ ...p, year: e.target.value }))}
              sx={{ minWidth: 110 }}
            >
              {yearOpts.map((y) => (
                <MenuItem key={y} value={y}>
                  {y}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="Квартал"
              value={periodVals.quarter}
              onChange={(e) => setPeriodVals((p) => ({ ...p, quarter: e.target.value }))}
              sx={{ minWidth: 170 }}
            >
              {QUARTER_OPTIONS.map((q) => (
                <MenuItem key={q.value} value={q.value}>
                  {q.label}
                </MenuItem>
              ))}
            </TextField>
          </>
        );
      case 'year':
        return (
          <TextField
            select
            size="small"
            label="Рік"
            value={periodVals.year}
            onChange={(e) => setPeriodVals((p) => ({ ...p, year: e.target.value }))}
            sx={{ minWidth: 110 }}
          >
            {yearOpts.map((y) => (
              <MenuItem key={y} value={y}>
                {y}
              </MenuItem>
            ))}
          </TextField>
        );
      case 'range':
        return (
          <>
            <TextField
              size="small"
              label="Дата від"
              type="date"
              value={periodVals.from}
              onChange={(e) => setPeriodVals((p) => ({ ...p, from: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              size="small"
              label="Дата до"
              type="date"
              value={periodVals.to}
              onChange={(e) => setPeriodVals((p) => ({ ...p, to: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap alignItems="center">
          <TextField
            select
            size="small"
            label="Тип періоду"
            value={periodType}
            onChange={(e) => setPeriodType(e.target.value)}
            sx={{ minWidth: 150 }}
          >
            {PERIOD_TYPE_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
          {renderPeriodInputs()}
          {report === 'customers' && (
            <TextField
              select
              size="small"
              label="Замовник для деталізації"
              value={drillCustomer}
              onChange={(e) => setDrillCustomer(e.target.value)}
              sx={{ minWidth: 220 }}
            >
              <MenuItem value="">Усі</MenuItem>
              {customerOptions.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </TextField>
          )}
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Період: {start.toLocaleDateString('uk-UA')} – {end.toLocaleDateString('uk-UA')}
        </Typography>
      </Paper>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }} alignItems="flex-start">
        <Box sx={{ minWidth: 280 }}>
          <MetricsTable rows={report === 'orders' ? ordersMetrics : customersMetrics} />
        </Box>

        <TableContainer component={Paper} variant="outlined" sx={{ flex: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{report === 'orders' ? 'Виріб' : 'Замовник'}</TableCell>
                <TableCell align="right">Кількість</TableCell>
                <TableCell align="right">Сума</TableCell>
                <TableCell align="right">Позицій</TableCell>
                <TableCell>Остання дата замовлення</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(report === 'orders' ? byProduct : byCustomer).length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography color="text.secondary" sx={{ py: 2 }}>
                      Немає даних за цей період
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {(report === 'orders' ? byProduct : byCustomer).map((r) => (
                    <TableRow key={r.name} hover>
                      <TableCell>{r.name}</TableCell>
                      <TableCell align="right">{r.quantity}</TableCell>
                      <TableCell align="right">{formatAmount(r.amount)}</TableCell>
                      <TableCell align="right">{r.positions}</TableCell>
                      <TableCell>{formatDateOnly(r.lastDate)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Всього</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {(report === 'orders' ? byProduct : byCustomer).reduce((s, r) => s + r.quantity, 0)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {formatAmount(
                        (report === 'orders' ? byProduct : byCustomer).reduce((s, r) => s + r.amount, 0),
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {(report === 'orders' ? byProduct : byCustomer).reduce((s, r) => s + r.positions, 0)}
                    </TableCell>
                    <TableCell />
                  </TableRow>
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>

      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems="stretch">
        <TableContainer component={Paper} variant="outlined" sx={{ maxWidth: { lg: 380 } }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{chartGranularity === 'day' ? 'Дата' : 'Місяць'}</TableCell>
                <TableCell align="right">Кількість</TableCell>
                <TableCell align="right">Сума</TableCell>
                {report === 'customers' && <TableCell align="right">Позицій</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {chartRows.map((r) => (
                <TableRow key={r.label}>
                  <TableCell>{r.label}</TableCell>
                  <TableCell align="right">{r.quantity}</TableCell>
                  <TableCell align="right">{formatAmount(r.amount)}</TableCell>
                  {report === 'customers' && <TableCell align="right">{r.positions}</TableCell>}
                </TableRow>
              ))}
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Всього</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  {chartRows.reduce((s, r) => s + r.quantity, 0)}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  {formatAmount(chartRows.reduce((s, r) => s + r.amount, 0))}
                </TableCell>
                {report === 'customers' && (
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {chartRows.reduce((s, r) => s + r.positions, 0)}
                  </TableCell>
                )}
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        <MiniBarChart
          data={chartRows}
          title={`Замовлення за ${chartGranularity === 'day' ? 'днями' : 'місяцями'}`}
        />

        {report === 'customers' && (
          <Stack spacing={2}>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Квартал</TableCell>
                    <TableCell align="right">Кількість</TableCell>
                    <TableCell align="right">Сума</TableCell>
                    <TableCell align="right">Позицій</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {byQuarter.map((r) => (
                    <TableRow key={r.label}>
                      <TableCell>{r.label}</TableCell>
                      <TableCell align="right">{r.quantity}</TableCell>
                      <TableCell align="right">{formatAmount(r.amount)}</TableCell>
                      <TableCell align="right">{r.positions}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Всього</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {byQuarter.reduce((s, r) => s + r.quantity, 0)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {formatAmount(byQuarter.reduce((s, r) => s + r.amount, 0))}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {byQuarter.reduce((s, r) => s + r.positions, 0)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Рік</TableCell>
                    <TableCell align="right">Кількість</TableCell>
                    <TableCell align="right">Сума</TableCell>
                    <TableCell align="right">Позицій</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {byYear.map((r) => (
                    <TableRow key={r.label}>
                      <TableCell>{r.label}</TableCell>
                      <TableCell align="right">{r.quantity}</TableCell>
                      <TableCell align="right">{formatAmount(r.amount)}</TableCell>
                      <TableCell align="right">{r.positions}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Всього</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {byYear.reduce((s, r) => s + r.quantity, 0)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {formatAmount(byYear.reduce((s, r) => s + r.amount, 0))}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {byYear.reduce((s, r) => s + r.positions, 0)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        )}
      </Stack>
    </Box>
  );
}

export default OrdersAnalyticsPage;
